import { JSX, useEffect, useState } from "react";
import "../../styles/Authentication.scss";
import { ClubTermsLink } from "./ClubTermsLink";
import { RegistrationFormHeader } from "./RegistrationFormHeader";
import { useLabelsContext } from "../../context/LabelsContext";
import { useClubContext } from "../../context/ClubContext";
import { useConfigContext } from "../../context/ConfigContext";
import { ClubMember } from "../../models/club-member";
import {
	FIELDS_REQUIRING_AVAILABILITY_CHECK,
	useRegistrationForm,
} from "../../hooks/useRegistrationForm";
import { RegistrationResponse } from "../../models/registration-response";
import { ApiResponse, ApiStatus } from "../../models/api-response";
import {
	activateMember,
	getIframeURL,
	registerClubMember,
} from "../../services/registration-service";
import { FieldLabel } from "./FieldLabel";
import { DateSelect } from "./DateSelect";
import { Checkbox } from "../Checkbox";
import { Modal } from "../Modal";
import { useUserContext } from "../../context/UserContext";
import { AuthSuccess } from "../Authentication/AuthSuccess";
import { AnimatePresence } from "framer-motion";
import { emitEvent, PLUGIN_EVENTS } from "../../utils/pluginEvents";
import { SelectArrowIcon } from "../../svgIcons";
import { useCountries } from "../../hooks/useCountries";
import { IframeResponse } from "../../models/iframe-url-response";
import { PaymentIframe } from "./PaymentIframe";
import { SpouseFields } from "./SpouseFields";
import { getUrlOrigin, isValidIsraeliID } from "../../utils/helpers";
import { useAuthContext } from "../../context/AuthContext";
import { ActivateMemberResponse } from "../../models/activate-member-response";
import { useQuery } from "../../hooks/useQuery";
import {
	REGISTRATION_STEPS,
	RegistrationStep,
} from "../../models/registration-steps";
import { Country } from "../../models/country";
import { Authentication } from "../Authentication/Authentication";
import { Controller } from "react-hook-form";
import { dispatchAnalyticsEvent } from "../../analytics/dataLayer";
import { buildFlashyContact } from "../../flashy/flashy-helper";
import { FLASHY_EVENTS } from "../../flashy/FLASHY_EVENTS";
import { useFlashy } from "../../hooks/useFlashy";
import { isMobile } from "react-device-detect";

const RegistrationForm: React.FC = () => {
	const [isModalOpen, setIsModalOpen] = useState<boolean>(true);
	const { config } = useConfigContext();
	const { labels } = useLabelsContext();
	const { setUser } = useUserContext();
	const { club } = useClubContext();
	const { countries } = useCountries();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isTermsModalOpen, setIsTermsModalOpen] = useState<boolean>(false);
	const [isSuccessModalOpen, setIsSuccessModalOpen] =
		useState<boolean>(false);
	const [step, setStep] = useState<RegistrationStep>(
		REGISTRATION_STEPS.REGISTRATION,
	);
	const [iframeURL, setIframeURL] = useState<string | null>(null);
	const [clubMember, setClubMember] = useState<ClubMember>();
	const [error, setError] = useState<string | null>(null);
	const query = useQuery();
	const salesClerk: string | null = query.get("salesclerk");
	const hotelId: string | null = query.get("hotelid");
	const { interestedUser, loadingInterestedUser, interestedUserError } =
		useAuthContext();
	const { sendEvent: sendFlashyEcommerceEvent } = useFlashy();
	const {
		registrationForm,
		handleSubmit,
		errors,
		register,
		trigger,
		getFieldRequirement,
		getValues,
		control,
		availability,
		checkFieldAvailability,
		getErrorMessage,
		resetForm,
		buildClubMemberRequest,
		watch,
		setValue,
		triggerAutoFilledField,
		markAsTouched
	} = useRegistrationForm();

	const onSubmit = async (): Promise<void> => {
		setIsLoading(true);
		let clubMemberRequest: ClubMember = buildClubMemberRequest();

		const isUpdate: boolean = interestedUser?.MissingRequiredField ?? false;

		try {
			const registrationResponse: ApiResponse<RegistrationResponse> =
				await registerClubMember(
					clubMemberRequest,
					config,
					club?.bookingDomain,
					salesClerk ?? "",
					hotelId ?? "",
					isUpdate,
				);

			// Handle successful registration
			if (registrationResponse.Status === ApiStatus.Success) {
				if (isUpdate) {
					setStep(REGISTRATION_STEPS.SUCCESS);
					setIsSuccessModalOpen(true);
					return;
				}
				clubMemberRequest.GuestId =
					registrationResponse.Data.memberShip.GuestID ??
					registrationResponse.Data.memberID ??
					0;
				clubMemberRequest.clubJoinPrice = registrationResponse.Data.memberShip.ClubJoinPrice ?? 0;
				clubMemberRequest.clubRenewPrice = registrationResponse.Data.memberShip.ClubRenewPrice ?? 0;
				clubMemberRequest.currency = registrationResponse.Data.memberShip.Currency ?? "";
				clubMemberRequest.clubCode = registrationResponse.Data.memberShip.ClubCode ?? "";
				if (registrationResponse.Data.memberShip.IsClubActive || registrationResponse.Data.OctopusClubmember) {
					// free membership, complete registration
					await completeRegistration(clubMemberRequest);
				} else {
					// paid membership
					setClubMember(clubMemberRequest);
					await loadPaymentIframe(clubMemberRequest);
				}
			}
			setIsLoading(false);
		} catch (error) {
			alert(labels?.["ApiRegisterFailure"]);
		} finally {
			setIsLoading(false);
		}
	};
	const LoginPopupBtn = (): JSX.Element | null => {
		if (!labels?.["TryToLogin"]) return null;

		return (
			<button
				type='button'
				onClick={() => emitEvent(PLUGIN_EVENTS.SHOW_LOGIN_POPUP)}
				className='error-message key-TryToLogin'
			>
				{labels?.["TryToLogin"]}
			</button>
		);
	};
	const loadPaymentIframe = async (clubMember: ClubMember): Promise<void> => {
		const iframeResponse: IframeResponse = await getIframeURL(
			config,
			clubMember.GuestId,
			clubMember.email,
			"join",
			club?.bookingDomain || "",
		);

		if (iframeResponse.Success || iframeResponse.Obj.Target === "IFRAME") {
			setStep(REGISTRATION_STEPS.PAYMENT);
			setIframeURL(iframeResponse.Obj.PaymentIframeToRedirect || "");
		}
	};
	const dispatchRegistrationEvent = (clubMember: ClubMember): void => {
		dispatchAnalyticsEvent({
			event: "submit_customer_details",
			data: {
				signup_branch: window.location.hostname,
				signup_source: "website",
				action: "register",
				language: config?.language,
				loyalty_membership: true,
				agree_newsletters: clubMember.agretoMails,
			},
		});
	};

	const completeRegistration = async (
		clubMember: ClubMember,
	): Promise<void> => {
		resetForm();
		setUser({
			Name: clubMember.firstName,
			LoggedIn: true,
			Passport: clubMember?.passport ?? "",
			Email: clubMember.email,
		});
		setStep(REGISTRATION_STEPS.SUCCESS);
		setIsSuccessModalOpen(true);

		// emit registration event for other plugins
		emitEvent(PLUGIN_EVENTS.REGISTRATION_SUCCESS, clubMember);

		// analytics - registration event
		dispatchRegistrationEvent(clubMember);

		// flashy - create or update contact
		if (club?.newsletterType === "flashy") {
			sendFlashyEcommerceEvent(
				FLASHY_EVENTS.CREATE_OR_UPDATE,
				buildFlashyContact(club, clubMember, config?.language ?? "he"),
			);
		}
	};

	const handleMemberActivation = (iframeResponse: string) => {
		if (!clubMember) return;

		activateMember(
			config,
			clubMember?.GuestId,
			iframeResponse,
			false,
			salesClerk,
			hotelId,
			club?.bookingDomain,
		)
			.then((response: ApiResponse<ActivateMemberResponse>) => {
				if (response.Status === ApiStatus.Success) {
					completeRegistration(clubMember);
					return;
				}
				handleError(
					response?.Data?.msg || labels?.["ApiRegisterFailure"] || "",
				);
			})
			.catch(() => handleError(iframeResponse));
	};
	const handleIframeResponse = async (event: MessageEvent): Promise<void> => {
		if (!clubMember || !club?.bookingDomain) return;

		const allowedOrigins: string[] = [
			getUrlOrigin(club.bookingDomain),
			"https://be-core.secure-zimmer.net",
		];
		if (!allowedOrigins.includes(event.origin)) return;

		const messageObject = JSON.parse(event.data);

		if (messageObject.type == "PCITokenSuccess") {
			handleMemberActivation(messageObject.message);
		} else {
			handleError(messageObject.message);
		}
	};
	const handleError = (errorMessage: string) => {
		setError(errorMessage);
		setStep(REGISTRATION_STEPS.REGISTRATION);
	};

	const isInterestedUserFieldValid = (field: string): boolean => {
		if (field === "email") {
			const emailRegex = new RegExp(
				labels?.["MailRegex"] || "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
				"i",
			);

			return (
				Boolean(interestedUser?.Email) &&
				emailRegex.test(interestedUser?.Email ?? "")
			);
		}
		if (field === "passport") {
			return (
				Boolean(interestedUser?.Passport) &&
				isValidIsraeliID(interestedUser?.Passport ?? "")
			);
		}
		return true;
	};

	// listen for postMessage events from the payment iframe
	useEffect(() => {
		window.addEventListener("message", handleIframeResponse, false);
		return () => {
			window.removeEventListener("message", handleIframeResponse);
		};
	}, [clubMember]);

	// validate interested user "email" and "passport"
	useEffect(() => {
		if (interestedUser || loadingInterestedUser) return;

		for (const field of FIELDS_REQUIRING_AVAILABILITY_CHECK) {
			if (getValues(field)) checkFieldAvailability(field);
		}
	}, [interestedUser, loadingInterestedUser]);

	// error occurs while loading the interested user,
	useEffect(() => {
		if (interestedUserError) setError(interestedUserError);
	}, [interestedUserError]);

	if (loadingInterestedUser) return null;

	if (interestedUser && !interestedUser.verified && isModalOpen) {
		return <Authentication setIsModalOpen={setIsModalOpen} />;
	}

	return (
		<div className='registration-form-container'>
			<RegistrationFormHeader step={step} />
			{(step === REGISTRATION_STEPS.REGISTRATION ||
				step === REGISTRATION_STEPS.SUCCESS) && (
					<form
						onSubmit={handleSubmit(onSubmit)}
						className='simplex-club-form'
					>
						<div className='form-grid'>
							<div>
								{getErrorMessage("firstName") && (
									<span
										className={`error-message key-${getErrorMessage("firstName")?.key}`}
									>
										{getErrorMessage("firstName")?.message}
									</span>
								)}
								<div
									className={`form-field ${errors.firstName ? "has-error" : ""}`}
								>
									<input
										id='firstName'
										type='text'
										placeholder=' '
										{...register(
											"firstName",
											registrationForm.firstName,
										)}
										onFocus={() => markAsTouched("firstName")}
										onInput={() =>
											setTimeout(() => {
												triggerAutoFilledField("firstName");
											})
										}
										onBlur={() => trigger("firstName")}
									/>
									<FieldLabel
										field={"firstName"}
										label={labels?.["FirstName"]}
									/>
								</div>
							</div>
							<div>
								{getErrorMessage("lastName") && (
									<span
										className={`error-message key-${getErrorMessage("lastName")?.key}`}
									>
										{getErrorMessage("lastName")?.message}
									</span>
								)}
								<div
									className={`form-field ${errors.lastName ? "has-error" : ""}`}
								>
									<input
										id='lastName'
										type='text'
										placeholder=' '
										{...register(
											"lastName",
											registrationForm.lastName,
										)}
										onFocus={() => markAsTouched("lastName")}
										onInput={() =>
											setTimeout(() => {
												triggerAutoFilledField("lastName");
											})
										}
										onBlur={() => trigger("lastName")}
									/>
									<FieldLabel
										field={"lastName"}
										label={labels?.["LastName"]}
									/>
								</div>
							</div>
							<div>
								{getErrorMessage("email") && (
									<>
										<span
											className={`error-message key-${getErrorMessage("email")?.key}`}
										>
											{getErrorMessage("email")?.message}
										</span>
										{availability.email.exist &&
											!availability.email.inactive && (
											<>
												&nbsp;
												<LoginPopupBtn />
											</>
										)}
									</>
								)}
								<div
									className={`form-field ${errors.email ? "has-error" : ""}`}
								>
									<input
										id='email'
										type='email'
										placeholder=' '
										{...register(
											"email",
											registrationForm.email,
										)}
										onBlur={() =>
											!interestedUser &&
											checkFieldAvailability("email")
										}
										onFocus={() => markAsTouched("email")}
										onInput={() =>
											setTimeout(() => {
												triggerAutoFilledField("email");
											})
										}
										disabled={availability.email.checking}
										readOnly={
											interestedUser?.verified &&
											!interestedUser?.MissingRequiredField &&
											isInterestedUserFieldValid("email")
										}
									/>
									<FieldLabel
										field={"email"}
										label={labels?.["Email"]}
									/>
									{availability.email.checking && (
										<span className='loader primary'></span>
									)}
								</div>
							</div>
							<div>
								{getErrorMessage("phoneNumber") && (
									<span
										className={`error-message key-${getErrorMessage("phoneNumber")?.key}`}
									>
										{getErrorMessage("phoneNumber")?.message}
									</span>
								)}
								<div
									className={`form-field ${errors.phoneNumber ? "has-error" : ""}`}
								>
									<input
										id='phoneNumber'
										type='tel'
										placeholder=' '
										{...register(
											"phoneNumber",
											registrationForm.phoneNumber,
										)}
										onFocus={() => markAsTouched("phoneNumber")}
										onInput={() =>
											setTimeout(() => {
												triggerAutoFilledField(
													"phoneNumber",
												);
											})
										}
										onBlur={() => trigger("phoneNumber")}
									/>
									<FieldLabel
										field={"phoneNumber"}
										label={labels?.["Phone"] || "Phone number"}
									/>
								</div>
							</div>
							{getFieldRequirement("passport") !== "none" && (
								<div>
									{getErrorMessage("passport") && (
										<>
											<span
												className={`error-message key-${getErrorMessage("passport")?.key}`}
											>
												{
													getErrorMessage("passport")
														?.message
												}
											</span>
											{availability.passport.exist &&
												!availability.passport.inactive && (
												<>
													&nbsp;
													<LoginPopupBtn />
												</>
											)}
										</>
									)}
									<div
										className={`form-field ${errors.passport ? "has-error" : ""}`}
									>
										<input
											id='passport'
											type='text'
											inputMode='numeric'
											placeholder=' '
											{...register(
												"passport",
												registrationForm.passport,
											)}
											onBlur={() =>
												!interestedUser &&
												checkFieldAvailability("passport")
											}
											onFocus={() => markAsTouched("passport")}
											onInput={() =>
												setTimeout(() => {
													triggerAutoFilledField(
														"passport",
													);
												})
											}
											disabled={
												availability.passport.checking
											}
											readOnly={
												interestedUser?.verified &&
												!interestedUser?.MissingRequiredField &&
												isInterestedUserFieldValid(
													"passport",
												)
											}
										/>
										<FieldLabel
											field={"passport"}
											label={labels?.["passportCrm"]}
										/>
										{availability.passport.checking && (
											<span className='loader primary'></span>
										)}
									</div>
								</div>
							)}
							{getFieldRequirement("birthDate") !== "none" && (
								<div>
									<div
										className={`form-select ${errors.birthDate ? "has-error" : ""}`}
									>
										<FieldLabel
											field={"birthDate"}
											label={labels?.["BirthDayLabel"]}
										/>
										<Controller
											name='birthDate'
											control={control}
											rules={registrationForm.birthDate}
											render={({ field, fieldState }) => (
												<DateSelect
													field='birthDate'
													value={field.value}
													onChange={field.onChange}
													onBlur={field.onBlur}
													hasError={!!fieldState.error}
													minAge={18}
												/>
											)}
										/>
									</div>
								</div>
							)}
							{getFieldRequirement("city") !== "none" && (
								<div>
									{getErrorMessage("city") && (
										<span
											className={`error-message key-${getErrorMessage("city")?.key}`}
										>
											{getErrorMessage("city")?.message}
										</span>
									)}
									<div
										className={`form-field ${errors.city ? "has-error" : ""}`}
									>
										<input
											id='city'
											type='text'
											placeholder=' '
											{...register(
												"city",
												registrationForm.city,
											)}
											onBlur={() => trigger("city")}
										/>
										<FieldLabel field={"city"} label='city' />
									</div>
								</div>
							)}
							{getFieldRequirement("country") !== "none" && (
								<div className='form-select'>
									<FieldLabel
										field={"country"}
										label={labels?.["SelectCountry"]}
									/>
									<div>
										{getErrorMessage("country") && (
											<span
												className={`error-message key-${getErrorMessage("country")?.key}`}
											>
												{
													getErrorMessage("country")
														?.message
												}
											</span>
										)}
										<div className='club-form-select'>
											<select
												className={`form-field ${errors.country ? "has-error" : ""}`}
												id='country'
												{...register(
													"country",
													registrationForm.country,
												)}
												onBlur={() => trigger("country")}
											>
												<option value=''>
													{labels?.["SelectCountry"]}
												</option>
												{countries?.map((c: Country) => {
													return (
														<option
															key={c.countryIsoCode}
															value={c.countryName}
														>
															{c.countryName}
														</option>
													);
												})}
											</select>
											<SelectArrowIcon />
										</div>
									</div>
								</div>
							)}
						</div>
						{getFieldRequirement("spouseLastName") !== "none" &&
							getFieldRequirement("spouseFirstName") !== "none" &&
							getFieldRequirement("spouseBirthDate") !== "none" && (
								<div className='form-row'>
									<Checkbox
										id='showSpouseFields'
										label={labels?.["showspouse"] || ""}
										register={register(
											"showSpouseFields",
											registrationForm.showSpouseFields,
										)}
										onChange={() => {
											trigger("showSpouseFields");
										}}
										error={errors.showSpouseFields}
									/>
								</div>
							)}
						{getValues("showSpouseFields") && (
							<SpouseFields
								errors={errors}
								trigger={trigger}
								control={control}
								register={register}
								getErrorMessage={getErrorMessage}
								registrationForm={registrationForm}
								getFieldRequirement={getFieldRequirement}
								watch={watch}
								setValue={setValue}
							/>
						)}
						<div
							className='form-grid u-border-top-dotted'
							style={{ margin: "0" }}
						>
							<div>
								{getErrorMessage("termsAndConditions") && (
									<span
										className={`error-message key-${getErrorMessage("termsAndConditions")?.key}`}
									>
										{
											getErrorMessage("termsAndConditions")
												?.message
										}
									</span>
								)}
								{!interestedUser?.MissingRequiredField && (
									<>
										<div className='form-row'>
											<Checkbox
												id='termsAndConditions'
												label={
													<>
														{
															labels?.[
															"AgreeToClubTermsStart"
															]
														}
														&nbsp;
														<ClubTermsLink
															setIsModalOpen={
																setIsTermsModalOpen
															}
														/>
													</>
												}
												register={register(
													"termsAndConditions",
													registrationForm.termsAndConditions,
												)}
												onChange={() => {
													trigger("termsAndConditions");
												}}
												error={errors.termsAndConditions}
											/>
											{isTermsModalOpen && (
												<Modal
													onClose={() =>
														setIsTermsModalOpen(false)
													}
													title={
														labels?.[
														"TermsAndConditions"
														]
													}
												>
													<span
														dangerouslySetInnerHTML={
															club?.clubTermsText
																? {
																	__html: club.clubTermsText,
																}
																: undefined
														}
													></span>
												</Modal>
											)}
										</div>
										{getErrorMessage("agreeToReceiveMails") && (
											<span
												className={`error-message key-${getErrorMessage("agreeToReceiveMails")?.key}`}
											>
												{
													getErrorMessage(
														"agreeToReceiveMails",
													)?.message
												}
											</span>
										)}
										<div
											className='form-row'
											style={{ margin: "0" }}
										>
											<Checkbox
												id='agreeToReceiveMails'
												label={
													labels?.[
													"AgreeToReceiveMails"
													] || ""
												}
												register={register(
													"agreeToReceiveMails",
													registrationForm.agreeToReceiveMails,
												)}
												onChange={() => {
													trigger("agreeToReceiveMails");
												}}
												error={errors.agreeToReceiveMails}
											/>
										</div>
									</>
								)}
							</div>
							<button
								type='submit'
								className='form-submit'
								disabled={
									isLoading ||
									availability.email.checking ||
									availability.passport.checking
								}
							>
								{isLoading && (
									<span className='loader secondary'></span>
								)}
								{interestedUser?.MissingRequiredField
									? labels?.["ConfirmDetails"]
									: labels?.["CM_join"]}
							</button>
						</div>
					</form>
				)}
			{step === REGISTRATION_STEPS.PAYMENT && (
				<>
					{clubMember && clubMember.clubJoinPrice && clubMember.clubJoinPrice > 0 && (
						<div className={`d-flex flex-column gap-8 payment-iframe-header ${isMobile ? 'text-center' : ''}`}>
							<span className='ff-bold'>
								{labels?.["SecurePayment"]}
							</span>
							<div>
								<span className='ff-bold'>
									{labels?.["PayToComplete"]}:&nbsp;
								</span>
								<span className='ff-medium'>
									{clubMember.clubJoinPrice + (clubMember?.currency || "")}
								</span>
							</div>
						</div>
					)}
					<PaymentIframe iframeUrl={iframeURL || ""} />
				</>
			)}
			<AnimatePresence>
				{step === REGISTRATION_STEPS.SUCCESS && isSuccessModalOpen && (
					<Modal onClose={() => setIsSuccessModalOpen(false)}>
						<AuthSuccess setIsModalOpen={setIsSuccessModalOpen} />
					</Modal>
				)}
			</AnimatePresence>
			<AnimatePresence key='transaction-failed'>
				{error && (
					<Modal
						onClose={() => setError(null)}
						title={error}
						isAlert
					/>
				)}
			</AnimatePresence>
		</div>
	);
};
export default RegistrationForm;
